import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Bot, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, Loader2, RefreshCw, XCircle } from "lucide-react";
import { apiClient } from "@/lib/api-client";
import { useStudio } from "@/contexts/StudioContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket, WS_AGENT_WORKFLOW_EVENT } from "@/contexts/WebSocketContext";
import { getDataSectionBasePath } from "@/lib/vertical";
import { useTranslatedVerticalLabels } from "@/lib/i18n/use-translated-vertical-labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { studioSectionTitleClassName, modalAttrValueClassName } from "@/lib/modal-typography";
import { TraceRunsListSkeleton } from "@/components/skeletons";
import {
  appendWorkflowProgressEvent,
  buildWorkflowTopologyState,
  compareBySequenceTimestampAndId,
  decisionStepDisplay,
  getCascadeOfFromMetadata,
  getSequenceFromMetadata,
  getTierDecisionAttempt,
  filterRedundantWorkflowAuditEvents,
  formatAuditMetadataString,
  formatToolOutcomeEntries,
  getAuditHumanSummary,
  mergeAuditWithLiveWorkflowEvents,
  orchestratorLlmCallsByOperation,
  shortTraceHash,
  truncateTraceDetailText,
  type OrchestratorTraceBlockLike,
  type TierDecisionAttempt,
} from "@/components/studio-data/agent-trace-view-helpers";

interface TraceAuditEvent {
  eventId: string;
  agentRunId: string;
  timestamp: string;
  eventType: string;
  studioId: string;
  platform: string;
  actor: string;
  traceId: string;
  spanId: string;
  metadata: Record<string, unknown>;
  humanSummary?: string | null;
}

interface AgentRunTraceData {
  agentRunId: string;
  trace: unknown | null;
  auditEvents: TraceAuditEvent[];
  artifacts: {
    screenshots: string[];
    llmDecisionsKey: string | null;
    failureScreenshot: string | null;
    video: string | null;
    har: string | null;
    playwrightTrace: string | null;
  };
  presignedUrls: {
    stepScreenshots: Array<{ key: string; url: string | null }>;
    failureScreenshot: string | null;
    llmDecisionsJson: string | null;
    video: string | null;
    har: string | null;
    playwrightTrace: string | null;
  } | null;
  llmDecisions: unknown[] | null;
  llmUsageLogs: Array<{
    id: string;
    operation: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    costUsd: number;
    createdAt: string;
  }>;
  orchestratorTrace: OrchestratorTraceBlockLike | null;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function formatTraceUsd(usd: number): string {
  const v = Number(usd) || 0;
  return v < 0.01 ? v.toFixed(4) : v.toFixed(3);
}

interface AgentRunListRow {
  agentRunId: string;
  startedAt: string;
  endedAt: string;
  platform: string;
  lastEventType: string;
  lastEventSummary?: string;
  eventCount: number;
  estimatedLlmCostUsd?: number;
  llmUsageCallCount?: number;
}

interface ListRunsResponse {
  clickHouseAvailable: boolean;
  runs: AgentRunListRow[];
  listedRunsEstimatedLlmCostUsd?: number;
}

interface WorkflowProgressPayload {
  type: "agent_workflow_progress";
  eventId: string;
  timestamp: string;
  studioId: string;
  agentRunId: string;
  platform: string;
  actor: "orchestrator" | "vision_agent";
  workflowType: "langgraph_orchestrator" | "vision_goal_runner";
  stage: "run" | "node" | "step" | "phase";
  status: "started" | "completed" | "failed";
  stepId: string;
  label?: string;
  sequence?: number;
  metadata?: Record<string, unknown>;
}

export type AgentTraceViewLayout = "page" | "embedded";

export type AgentTraceViewProps = {
  layout?: AgentTraceViewLayout;
  /** When embedded, auto-load this run once mounted (e.g. from sync). */
  seedRunId?: string | null;
};

export function AgentTraceView({ layout = "page", seedRunId = null }: AgentTraceViewProps) {
  const { t, i18n } = useTranslation();
  const { connected } = useWebSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const runFromQuery = layout === "page" ? (searchParams.get("run")?.trim() ?? "") : "";
  const { user } = useAuth();
  const labels = useTranslatedVerticalLabels(user?.accountVertical);
  const { activeStudioId, isAllStudios } = useStudio();
  const [runIdInput, setRunIdInput] = useState(runFromQuery);
  const [loadedRunId, setLoadedRunId] = useState<string | null>(null);
  const [data, setData] = useState<AgentRunTraceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listDays, setListDays] = useState(30);
  const [runsList, setRunsList] = useState<AgentRunListRow[]>([]);
  const [listedRunsEstimatedLlmCostUsd, setListedRunsEstimatedLlmCostUsd] = useState<number | null>(null);
  const [expandedTimelineItems, setExpandedTimelineItems] = useState<Record<string, boolean>>({});
  const [orchestratorPanelOpen, setOrchestratorPanelOpen] = useState(true);
  const [clickHouseAvailable, setClickHouseAvailable] = useState<boolean | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [liveWorkflowEvents, setLiveWorkflowEvents] = useState<WorkflowProgressPayload[]>([]);
  const embeddedSeedHandledRef = useRef<string | null>(null);
  const seenWorkflowEventIdsRef = useRef<Set<string>>(new Set());
  const prevConnectedRef = useRef<boolean>(connected);
  const [expandedTechnicalDetails, setExpandedTechnicalDetails] = useState<Record<string, boolean>>({});

  const listDaysId = layout === "page" ? "trace-list-days" : "trace-list-days-embedded";
  const runInputId = layout === "page" ? "agent-run-id" : "agent-run-id-embedded";

  const formatTraceDt = useCallback(
    (iso: string) => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(i18n.language);
    },
    [i18n.language],
  );

  useEffect(() => {
    if (layout !== "page") return;
    if (runFromQuery) setRunIdInput(runFromQuery);
  }, [runFromQuery, layout]);

  const canLoad = useMemo(() => {
    if (!activeStudioId || activeStudioId === "all") return false;
    return UUID_RE.test(runIdInput.trim());
  }, [activeStudioId, runIdInput]);

  const loadTrace = useCallback(
    async (id: string, options?: { syncSearchParams?: boolean }) => {
      const syncSearchParams = options?.syncSearchParams ?? layout === "page";
      const trimmed = id.trim();
      if (!activeStudioId || activeStudioId === "all" || !UUID_RE.test(trimmed)) {
        setError(labels.agentTraceLoadErrorNeedsScope);
        return;
      }
      setLoading(true);
      setError(null);
      setData(null);
      setLoadedRunId(trimmed);
      setRunIdInput(trimmed);
      if (syncSearchParams) {
        setSearchParams(trimmed ? { run: trimmed } : {}, { replace: true });
      }
      const res = await apiClient<AgentRunTraceData>(
        `/observability/agent-runs/${encodeURIComponent(trimmed)}/trace`
      );
      setLoading(false);
      if (res.success && res.data) {
        setData(res.data);
        return;
      }
      const msg =
        (res.error as { detail?: string; message?: string } | undefined)?.detail ??
        (res.error as { message?: string } | undefined)?.message ??
        t("studioData.tabs.trace.loadError");
      setError(msg);
    },
    [activeStudioId, setSearchParams, layout, labels.agentTraceLoadErrorNeedsScope, t]
  );

  const loadRunList = useCallback(async () => {
    if (!activeStudioId || activeStudioId === "all") {
      setRunsList([]);
      setClickHouseAvailable(null);
      return;
    }
    setListLoading(true);
    const res = await apiClient<ListRunsResponse>(
      `/observability/agent-runs?days=${encodeURIComponent(String(listDays))}&limit=100`
    );
    setListLoading(false);
    if (res.success && res.data) {
      setRunsList(res.data.runs);
      setClickHouseAvailable(res.data.clickHouseAvailable);
      setListedRunsEstimatedLlmCostUsd(
        typeof res.data.listedRunsEstimatedLlmCostUsd === "number" ? res.data.listedRunsEstimatedLlmCostUsd : null
      );
      return;
    }
    setRunsList([]);
    setListedRunsEstimatedLlmCostUsd(null);
    setClickHouseAvailable(false);
  }, [activeStudioId, listDays]);

  useEffect(() => {
    if (isAllStudios || !activeStudioId || activeStudioId === "all") return;
    void loadRunList();
  }, [activeStudioId, isAllStudios, loadRunList]);

  useEffect(() => {
    if (layout !== "page") return;
    if (isAllStudios || !runFromQuery || !UUID_RE.test(runFromQuery)) return;
    if (!activeStudioId || activeStudioId === "all") return;
    void loadTrace(runFromQuery);
  }, [runFromQuery, activeStudioId, isAllStudios, layout, loadTrace]);

  useEffect(() => {
    if (layout !== "embedded") return;
    if (!seedRunId || !UUID_RE.test(seedRunId)) return;
    if (!activeStudioId || activeStudioId === "all") return;
    if (embeddedSeedHandledRef.current === seedRunId) return;
    embeddedSeedHandledRef.current = seedRunId;
    void loadTrace(seedRunId, { syncSearchParams: false });
  }, [layout, seedRunId, activeStudioId, loadTrace]);

  useEffect(() => {
    const onWorkflowProgress = (event: Event) => {
      const detail = (event as CustomEvent<WorkflowProgressPayload>).detail;
      setLiveWorkflowEvents((prev) =>
        appendWorkflowProgressEvent(prev, detail, loadedRunId, seenWorkflowEventIdsRef.current),
      );
    };

    window.addEventListener(WS_AGENT_WORKFLOW_EVENT, onWorkflowProgress as EventListener);
    return () => window.removeEventListener(WS_AGENT_WORKFLOW_EVENT, onWorkflowProgress as EventListener);
  }, [loadedRunId]);

  useEffect(() => {
    seenWorkflowEventIdsRef.current = new Set();
    setLiveWorkflowEvents([]);
  }, [loadedRunId, activeStudioId]);

  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = connected;
    if (!wasConnected && connected && loadedRunId) {
      void loadTrace(loadedRunId, { syncSearchParams: false });
    }
  }, [connected, loadedRunId, loadTrace]);

  const liveRunStatus = useMemo<"running" | "completed" | "failed" | "idle">(() => {
    if (liveWorkflowEvents.length === 0) return "idle";
    const runEvents = liveWorkflowEvents.filter((event) => event.stage === "run");
    if (runEvents.some((event) => event.status === "failed")) return "failed";
    if (runEvents.some((event) => event.status === "completed")) return "completed";
    if (runEvents.some((event) => event.status === "started")) return "running";
    return "idle";
  }, [liveWorkflowEvents]);

  useEffect(() => {
    if (!loadedRunId || liveRunStatus !== "running") return;
    const interval = window.setInterval(() => {
      void loadTrace(loadedRunId, { syncSearchParams: false });
    }, 15000);
    return () => window.clearInterval(interval);
  }, [loadedRunId, liveRunStatus, loadTrace]);

  const mergedAuditEvents = useMemo<TraceAuditEvent[]>(() => {
    const merged = mergeAuditWithLiveWorkflowEvents(data?.auditEvents ?? [], liveWorkflowEvents);
    return filterRedundantWorkflowAuditEvents(merged);
  }, [data?.auditEvents, liveWorkflowEvents]);

  useEffect(() => {
    setExpandedTechnicalDetails({});
  }, [loadedRunId]);

  // Build unified timeline merging audit events, LLM decisions, and screenshots
  const timeline = useMemo(() => {
    if (!data) return [];

    const fmtTime = (iso: string) => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime())
        ? ""
        : d.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
    };

    /** Keys look like `{runId}/steps/{stepIndex}-{screenshotIndex}-slug.png`. */
    type ParsedStepShot = { key: string; url: string | null; label: string; stepIndex: number; shotSeq: number };
    const parsedStepShots: ParsedStepShot[] = [];
    if (data.presignedUrls?.stepScreenshots) {
      for (const ss of data.presignedUrls.stepScreenshots) {
        const filename = ss.key.split("/").pop() ?? "";
        const m = filename.match(/^(\d+)-(\d+)-/);
        if (!m) continue;
        const stepIndex = Number.parseInt(m[1]!, 10);
        const shotSeq = Number.parseInt(m[2]!, 10);
        const label = filename.replace(/\.png$/i, "").replace(/^\d+-\d+-/, "").replace(/_/g, " ");
        parsedStepShots.push({ key: ss.key, url: ss.url, label, stepIndex, shotSeq });
      }
    }
    const shotsByStepIndex = new Map<number, ParsedStepShot[]>();
    for (const s of parsedStepShots) {
      if (!shotsByStepIndex.has(s.stepIndex)) shotsByStepIndex.set(s.stepIndex, []);
      shotsByStepIndex.get(s.stepIndex)!.push(s);
    }
    for (const arr of shotsByStepIndex.values()) {
      arr.sort((a, b) => a.shotSeq - b.shotSeq);
    }

    const toShot = (s: ParsedStepShot): { key: string; url: string | null; label: string } => ({
      key: s.key,
      url: s.url,
      label: s.label,
    });

    type TItem = {
      id: string;
      kind: "audit" | "llm" | "usage";
      timestamp: string;
      sequence?: number | null;
      time: string;
      isFailed: boolean;
      isCompleted: boolean;
      // audit
      eventType?: string;
      humanSummary?: string;
      metaStr?: string;
      tierDecision?: TierDecisionAttempt;
      cascadeOf?: string;
      // llm
      action?: string;
      stepIndex?: number | null;
      description?: string;
      tier?: string;
      selector?: string;
      error?: string;
      durationMs?: number;
      inputTokens?: number;
      outputTokens?: number;
      totalTokens?: number;
      estimatedCostUsd?: number;
      model?: string;
      executionRoute?: string;
      interactionMode?: string;
      subFlow?: string;
      method?: string;
      llmConfidence?: number;
      selfHealed?: boolean;
      commandsExecuted?: string[];
      systemPrompt?: string;
      userPromptText?: string;
      responseText?: string;
      temperature?: number;
      maxTokens?: number;
      screenshots: Array<{ key: string; url: string | null; label: string }>;
      // usage (orchestrator LLM billing rows)
      operation?: string;
      intent?: string;
      confidence?: number;
      promptKey?: string;
      promptKind?: string;
      promptSummary?: string;
      promptHash?: string;
      reasoningExcerpt?: string;
    };

    const items: TItem[] = [];
    const llmCallByOp = orchestratorLlmCallsByOperation(data.orchestratorTrace?.llmCalls);

    for (const e of mergedAuditEvents) {
      const tierDecision = getTierDecisionAttempt(e.metadata);
      const cascadeOf = getCascadeOfFromMetadata(e.metadata);
      const humanSummary = tierDecision ? undefined : getAuditHumanSummary(e) ?? undefined;
      const metaStr = tierDecision ? "" : formatAuditMetadataString(e.metadata);
      items.push({
        id: e.eventId,
        kind: "audit",
        timestamp: e.timestamp,
        sequence: getSequenceFromMetadata(e.metadata),
        time: fmtTime(e.timestamp),
        eventType: e.eventType,
        humanSummary,
        metaStr,
        ...(tierDecision ? { tierDecision } : {}),
        ...(cascadeOf ? { cascadeOf } : {}),
        isFailed: tierDecision ? tierDecision.outcome === "fail" : (e.eventType.includes("failed") || e.eventType.includes("error")),
        isCompleted: tierDecision ? tierDecision.outcome === "success" : e.eventType.includes("completed"),
        screenshots: [],
      });
    }

    const llmDecisions = data.llmDecisions ?? [];
    const decisionIndicesByStep = new Map<number, number[]>();
    const llmItems: TItem[] = [];

    for (let i = 0; i < llmDecisions.length; i++) {
      const d = llmDecisions[i] as Record<string, unknown>;
      const stepIdx = d.stepIndex as number | undefined;
      const action = (d.action as string) ?? "";
      if (typeof stepIdx === "number") {
        if (!decisionIndicesByStep.has(stepIdx)) decisionIndicesByStep.set(stepIdx, []);
        decisionIndicesByStep.get(stepIdx)!.push(i);
      }
      llmItems.push({
        id: `llm-${i}`,
        kind: "llm",
        timestamp: (d.timestamp as string) ?? "",
        sequence: null,
        time: fmtTime((d.timestamp as string) ?? ""),
        action,
        stepIndex: stepIdx ?? null,
        description: (d.description as string) ?? "",
        tier: (d.tier as string) ?? "",
        selector: (d.selector as string) || undefined,
        error: (d.error as string) || undefined,
        durationMs: d.durationMs as number | undefined,
        inputTokens: d.inputTokens as number | undefined,
        outputTokens: d.outputTokens as number | undefined,
        totalTokens: d.totalTokens as number | undefined,
        estimatedCostUsd: d.estimatedCostUsd as number | undefined,
        model: (d.model as string) || undefined,
        executionRoute: (d.executionRoute as string) || undefined,
        interactionMode: (d.interactionMode as string) || undefined,
        subFlow: (d.subFlow as string) || undefined,
        method: (d.method as string) || undefined,
        llmConfidence: typeof d.confidence === "number" ? d.confidence : undefined,
        selfHealed: d.selfHealed === true,
        commandsExecuted: Array.isArray(d.commandsExecuted)
          ? (d.commandsExecuted as string[]).filter((c) => typeof c === "string" && c.trim())
          : undefined,
        systemPrompt: typeof d.systemPrompt === "string" ? d.systemPrompt : undefined,
        userPromptText: typeof d.userPromptText === "string" ? d.userPromptText : undefined,
        responseText: typeof d.responseText === "string" ? d.responseText : undefined,
        temperature: typeof d.temperature === "number" ? d.temperature : undefined,
        maxTokens: typeof d.maxTokens === "number" ? d.maxTokens : undefined,
        isFailed: action === "step_failed" || action === "error",
        isCompleted: action === "step_completed" || action === "completed",
        screenshots: [],
      });
    }

    for (const [step, decIndices] of decisionIndicesByStep) {
      const shots = shotsByStepIndex.get(step) ?? [];
      for (let k = 0; k < decIndices.length; k++) {
        const di = decIndices[k]!;
        const shot = shots[k];
        if (shot) llmItems[di]!.screenshots = [toShot(shot)];
      }
      if (shots.length > decIndices.length) {
        const lastDi = decIndices[decIndices.length - 1]!;
        const extra = shots.slice(decIndices.length).map(toShot);
        llmItems[lastDi]!.screenshots = [...llmItems[lastDi]!.screenshots, ...extra];
      }
    }

    items.push(...llmItems);

    if (data.llmUsageLogs) {
      for (const u of data.llmUsageLogs) {
        const meta = llmCallByOp.get(u.operation);
        items.push({
          id: `usage-${u.id}`,
          kind: "usage",
          timestamp: u.createdAt,
          sequence: null,
          time: fmtTime(u.createdAt),
          operation: u.operation,
          model: u.model,
          inputTokens: u.inputTokens,
          outputTokens: u.outputTokens,
          totalTokens: u.totalTokens,
          estimatedCostUsd: u.costUsd,
          temperature: meta?.temperature,
          maxTokens: meta?.maxTokens ?? undefined,
          intent: meta?.intent ?? undefined,
          confidence: meta?.confidence ?? undefined,
          promptKey: meta?.promptKey,
          promptKind: meta?.promptKind,
          promptSummary: meta?.promptSummary ?? undefined,
          promptHash: meta?.promptHash ?? undefined,
          reasoningExcerpt: meta?.reasoningExcerpt ?? undefined,
          isFailed: false,
          isCompleted: false,
          screenshots: [],
        });
      }
    }

    items.sort((a, b) =>
      compareBySequenceTimestampAndId(
        { sequence: a.sequence ?? null, timestamp: a.timestamp, eventId: a.id },
        { sequence: b.sequence ?? null, timestamp: b.timestamp, eventId: b.id },
      ),
    );
    return items;
  }, [data, i18n.language, mergedAuditEvents]);

  /** Sum of LLM billing rows for this run (same source as per-step usage lines). */
  const totalLlmCostUsd = useMemo(() => {
    if (!data?.llmUsageLogs?.length) return null;
    const sum = data.llmUsageLogs.reduce((s, u) => s + (Number(u.costUsd) || 0), 0);
    return sum;
  }, [data]);

  const runsListTotalEstimatedLlmUsd = useMemo(() => {
    if (listedRunsEstimatedLlmCostUsd != null) return listedRunsEstimatedLlmCostUsd;
    return runsList.reduce((s, r) => s + (Number(r.estimatedLlmCostUsd) || 0), 0);
  }, [listedRunsEstimatedLlmCostUsd, runsList]);

  const timelineMaxH = layout === "embedded" ? "max-h-[min(360px,45vh)]" : "max-h-[600px]";

  const inner = (
    <>
      {layout === "page" && (
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`${getDataSectionBasePath(user?.accountVertical)}?tab=classes`}
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("studioData.tabs.trace.backToClasses")}
          </Link>
        </div>
      )}

      {layout === "page" ? (
        <div>
          <h1 className="text-xl font-semibold text-foreground">{t("studioData.tabs.trace.pageTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("studioData.tabs.trace.pageSubtitle")}</p>
        </div>
      ) : (
        <p className="text-[12px] leading-5 text-muted-foreground">{t("studioData.tabs.trace.pageSubtitle")}</p>
      )}

      {isAllStudios && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground">
          {t("studioData.tabs.trace.selectStudioForTraces")}
        </div>
      )}

      {!isAllStudios && activeStudioId && activeStudioId !== "all" && (
        <section className="rounded-2xl border border-border/50 bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className={studioSectionTitleClassName}>{t("studioData.tabs.trace.recentRuns")}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[12px] text-muted-foreground whitespace-nowrap">
                {t("studioData.tabs.trace.window")}
              </span>
              <Select value={String(listDays)} onValueChange={(v) => setListDays(Number(v))}>
                <SelectTrigger className="h-8 w-[110px] rounded-xl text-[12px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[7, 30, 90, 180, 365].map((d) => (
                    <SelectItem key={d} value={String(d)} className="text-[12px]">
                      {t("studioData.tabs.trace.daysOption", { count: d })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 rounded-xl text-[12px]"
                disabled={listLoading}
                onClick={() => void loadRunList()}
              >
                {listLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {t("studioData.tabs.trace.refresh")}
              </Button>
            </div>
          </div>
          {clickHouseAvailable === false && (
            <p className="text-[12px] text-muted-foreground mb-3">{t("studioData.tabs.trace.clickHouseNoList")}</p>
          )}
          {clickHouseAvailable && runsList.length === 0 && !listLoading && (
            <p className="text-[12px] text-muted-foreground mb-3">{labels.agentTraceNoAuditEventsForUnit}</p>
          )}
          <div className="max-h-[min(360px,50vh)] overflow-auto rounded-xl border border-border/40">
            {listLoading && runsList.length === 0 ? (
              <div className="py-2">
                <TraceRunsListSkeleton />
                <p className="px-4 pt-2 text-center text-sm text-muted-foreground" aria-hidden="true">
                  {t("studioData.tabs.trace.loadingRuns")}
                </p>
              </div>
            ) : (
              <table className="w-full text-left">
                <thead className="sticky top-0 z-[1] border-b border-border/40 bg-muted/50 backdrop-blur-sm">
                  <tr>
                    <th className="px-5 py-3 text-left text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{t("studioData.tabs.trace.lastActivity")}</th>
                    <th className="px-5 py-3 text-left text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{t("studioData.tabs.trace.platform")}</th>
                    <th className="px-5 py-3 text-left text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{t("studioData.tabs.trace.lastEvent")}</th>
                    <th className="px-5 py-3 text-right text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{t("studioData.tabs.trace.estimatedLlmCostColumn")}</th>
                    <th className="px-5 py-3 text-right text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">{t("studioData.tabs.trace.events")}</th>
                  </tr>
                </thead>
                <tbody>
                  {runsList.map((row) => {
                    const selected = loadedRunId === row.agentRunId;
                    return (
                      <tr
                        key={row.agentRunId}
                        className={cn(
                          "cursor-pointer border-b border-border/30 last:border-0 transition-colors hover:bg-muted/20",
                          selected && "bg-primary/10 hover:bg-primary/15"
                        )}
                        onClick={() => void loadTrace(row.agentRunId)}
                      >
                        <td className="px-5 py-3.5 align-top">
                          <span className="block text-sm font-semibold text-foreground leading-5">{formatTraceDt(row.endedAt)}</span>
                          <span className="block font-mono text-[10px] text-muted-foreground break-all mt-0.5">
                            {row.agentRunId}
                          </span>
                        </td>
                        <td className={cn("px-5 py-3.5", modalAttrValueClassName)}>{row.platform}</td>
                        <td className={cn("px-5 py-3.5 align-top max-w-xs", modalAttrValueClassName)}>
                          <span className="block text-sm leading-5">
                            {row.lastEventSummary ?? row.lastEventType}
                          </span>
                          {row.lastEventSummary && row.lastEventType !== row.lastEventSummary ? (
                            <span className="block font-mono text-[10px] text-muted-foreground mt-0.5">
                              {row.lastEventType}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-5 py-3.5 text-right align-top tabular-nums">
                          <span className="block text-sm font-medium text-foreground leading-5">
                            ${formatTraceUsd(row.estimatedLlmCostUsd ?? 0)}
                          </span>
                          {(row.llmUsageCallCount ?? 0) > 0 && (
                            <span className="block text-[10px] text-muted-foreground font-normal leading-4">
                              {t("studioData.tabs.trace.llmCallsCount", { count: row.llmUsageCallCount ?? 0 })}
                            </span>
                          )}
                        </td>
                        <td className={cn("px-5 py-3.5 text-right tabular-nums", modalAttrValueClassName)}>{row.eventCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {!listLoading && runsList.length > 0 && (
            <div className="mt-3 space-y-1 text-[12px] text-muted-foreground">
              <p>
                {t("studioData.tabs.trace.runsListTotalEstimatedLlmCost", {
                  amount: `$${formatTraceUsd(runsListTotalEstimatedLlmUsd)}`,
                })}
              </p>
              <p className="text-[11px] leading-snug">{t("studioData.tabs.trace.runsListCostDisclaimer")}</p>
            </div>
          )}
        </section>
      )}

      <div className="rounded-2xl border border-border/50 bg-card p-5">
        <div className="space-y-1.5 mb-3">
          <Label htmlFor={runInputId} className="text-[13px] font-medium">
            {t("studioData.tabs.trace.runIdLabel")}
          </Label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            id={runInputId}
            value={runIdInput}
            onChange={(e) => setRunIdInput(e.target.value)}
            placeholder={t("studioData.tabs.trace.runIdPlaceholder")}
            className="min-w-[280px] flex-1 h-10 rounded-xl font-mono text-sm"
          />
          <Button
            size="sm"
            disabled={!canLoad || loading}
            onClick={() => void loadTrace(runIdInput)}
            className="h-10 gap-1.5 rounded-xl px-4"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t("studioData.tabs.trace.loadTrace")}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-foreground">
          {error}
        </div>
      )}

      {data && loadedRunId && (() => {
        const hasFailed = mergedAuditEvents.some((e) => e.eventType === "agent_failed");
        const hasCompleted = mergedAuditEvents.some((e) => e.eventType === "agent_completed");
        const runStatus = hasFailed ? "error" : hasCompleted ? "success" : "unknown";
        const lastScreenshot = data.presignedUrls?.stepScreenshots.slice().reverse().find((s) => s.url);
        const completedMeta = mergedAuditEvents.find((e) => e.eventType === "agent_completed")?.metadata as
          | { durationMs?: number; recordsProcessed?: number }
          | undefined;
        const liveWorkflowCount = liveWorkflowEvents.length;
        const { workflowPath, latestStatusByStep } = buildWorkflowTopologyState(
          mergedAuditEvents,
          liveWorkflowEvents,
        );

        return (
          <div className="rounded-2xl border border-border/50 bg-card overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border/50 px-5 py-3">
              <div className="flex items-center gap-2">
                {runStatus === "success" ? (
                  <CheckCircle2 className="h-4 w-4 text-success" />
                ) : runStatus === "error" ? (
                  <XCircle className="h-4 w-4 text-destructive" />
                ) : (
                  <Bot className="h-4 w-4 text-primary" />
                )}
                <span className="text-[13px] font-semibold text-foreground">
                  {runStatus === "success"
                    ? t("studioData.tabs.trace.syncComplete")
                    : runStatus === "error"
                      ? t("studioData.tabs.trace.syncFailed")
                      : t("studioData.tabs.trace.runLoaded")}
                </span>
                <span className="font-mono text-[11px] text-muted-foreground ml-2 break-all">{data.agentRunId}</span>
                <span className="ml-2 rounded-full border border-border/50 bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {liveRunStatus === "running"
                    ? t("studioData.tabs.trace.workflowLiveBadge")
                    : t("studioData.tabs.trace.workflowReplayBadge")}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {data.presignedUrls?.playwrightTrace && (
                  <a
                    href={data.presignedUrls.playwrightTrace}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline underline-offset-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t("studioData.tabs.trace.linkPlaywrightTrace")}
                  </a>
                )}
                {data.presignedUrls?.video && (
                  <a
                    href={data.presignedUrls.video}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline underline-offset-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t("studioData.tabs.trace.linkRecording")}
                  </a>
                )}
                {data.presignedUrls?.har && (
                  <a
                    href={data.presignedUrls.har}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline underline-offset-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t("studioData.tabs.trace.linkHar")}
                  </a>
                )}
                {data.presignedUrls?.llmDecisionsJson && (
                  <a
                    href={data.presignedUrls.llmDecisionsJson}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline underline-offset-2"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t("studioData.tabs.trace.linkLlmDecisions")}
                  </a>
                )}
              </div>
            </div>

            <div className="border-b border-border/40 px-5 py-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[12px] font-semibold text-foreground">{t("studioData.tabs.trace.workflowTopologyTitle")}</p>
                <p className="text-[11px] text-muted-foreground">
                  {t("studioData.tabs.trace.workflowEventsCount", { count: liveWorkflowCount })}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {workflowPath.map((step) => {
                  const status = latestStatusByStep.get(step);
                  return (
                    <span
                      key={step}
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-[10px] font-medium",
                        status === "failed"
                          ? "border-destructive/40 bg-destructive/10 text-destructive"
                          : status === "completed"
                            ? "border-success/40 bg-success/10 text-success"
                            : status === "started"
                              ? "border-primary/40 bg-primary/10 text-primary"
                              : "border-border/40 bg-muted/30 text-muted-foreground"
                      )}
                    >
                      {step}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Progress Bar + Latest Screenshot */}
            <div className="px-5 pt-3 pb-1 flex gap-4 items-start">
              <div className="flex-1 min-w-0">
                <div className="h-2 w-full rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all duration-500 ease-out",
                      runStatus === "error" ? "bg-destructive" : runStatus === "success" ? "bg-success" : "bg-primary"
                    )}
                    style={{ width: "100%" }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1 text-right">
                  {runStatus === "success" && completedMeta?.recordsProcessed != null
                    ? t("studioData.tabs.trace.classesSynced", { count: completedMeta.recordsProcessed })
                    : t("studioData.tabs.trace.percentComplete")}
                  {completedMeta?.durationMs != null
                    ? t("studioData.tabs.trace.runDurationSuffix", { n: Math.round(completedMeta.durationMs / 1000) })
                    : null}
                  {totalLlmCostUsd != null && data.llmUsageLogs.length > 0 && (
                    <>
                      {" · "}
                      {t("studioData.tabs.trace.llmTotalLabel")}{" "}
                      <span className="font-medium text-foreground tabular-nums">
                        $
                        {totalLlmCostUsd < 0.01
                          ? totalLlmCostUsd.toFixed(4)
                          : totalLlmCostUsd.toFixed(3)}
                      </span>
                      {data.llmUsageLogs.length > 1 && (
                        <span className="text-muted-foreground/80">
                          {" "}
                          {t("studioData.tabs.trace.llmCallsCount", { count: data.llmUsageLogs.length })}
                        </span>
                      )}
                    </>
                  )}
                </p>
              </div>
              {lastScreenshot?.url && (
                <a
                  href={lastScreenshot.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-lg border border-border/40 overflow-hidden w-[140px] hover:ring-2 hover:ring-primary/30 transition-all"
                >
                  <img
                    src={lastScreenshot.url}
                    alt={t("studioData.tabs.trace.latestStepAlt")}
                    className="w-full h-auto max-h-[80px] object-contain bg-black/5"
                    loading="lazy"
                  />
                </a>
              )}
            </div>

            {data.orchestratorTrace && (
              <div className="mx-5 mb-3 rounded-lg border border-border/50 bg-muted/15">
                <div className="flex items-center gap-2 pr-4">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 px-4 py-2.5 text-left text-[12px] font-medium"
                    onClick={() => setOrchestratorPanelOpen((v) => !v)}
                    aria-expanded={orchestratorPanelOpen}
                  >
                    {orchestratorPanelOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                    {t("studioData.tabs.trace.orchestratorTraceHeading")}
                  </button>
                  {data.orchestratorTrace.conversationId && (
                    <Link
                      to={`/chats/${encodeURIComponent(data.orchestratorTrace.conversationId)}`}
                      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-primary hover:underline underline-offset-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {t("studioData.tabs.trace.openConversation")}
                    </Link>
                  )}
                </div>
                {orchestratorPanelOpen && (
                  <div className="border-t border-border/40 px-4 py-3 space-y-3 text-[11px]">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                      {data.orchestratorTrace.intent && (
                        <span>
                          {t("studioData.tabs.trace.intent")}:{" "}
                          <span className="font-medium text-foreground">{data.orchestratorTrace.intent}</span>
                        </span>
                      )}
                      {data.orchestratorTrace.confidence != null && (
                        <span>
                          {t("studioData.tabs.trace.confidence")}:{" "}
                          <span className="font-medium text-foreground">
                            {(data.orchestratorTrace.confidence * 100).toFixed(0)}%
                          </span>
                        </span>
                      )}
                      {data.orchestratorTrace.decisionOrigin && (
                        <span>
                          {t("studioData.tabs.trace.decisionOrigin")}:{" "}
                          <span className="font-medium text-foreground">{data.orchestratorTrace.decisionOrigin}</span>
                        </span>
                      )}
                      {data.orchestratorTrace.groundingVerdict && (
                        <span>
                          {t("studioData.tabs.trace.groundingVerdict")}:{" "}
                          <span className="font-medium text-foreground">{data.orchestratorTrace.groundingVerdict}</span>
                        </span>
                      )}
                      {data.orchestratorTrace.modelId && (
                        <span className="font-mono text-[10px]">{data.orchestratorTrace.modelId}</span>
                      )}
                    </div>
                    {data.orchestratorTrace.escalationReason && (
                      <p className="text-muted-foreground">
                        {t("studioData.tabs.trace.escalationReason")}: {data.orchestratorTrace.escalationReason}
                      </p>
                    )}
                    {data.orchestratorTrace.toolsCalled.length > 0 && (
                      <div>
                        <p className="font-medium text-muted-foreground mb-1">{t("studioData.tabs.trace.toolsCalled")}</p>
                        <ul className="space-y-0.5">
                          {data.orchestratorTrace.toolsCalled.map((tool) => {
                            const outcomeEntries = formatToolOutcomeEntries(tool.outcome);
                            return (
                              <li key={tool.tool} className="text-foreground">
                                <span className={tool.success ? "text-success" : "text-destructive"}>
                                  {tool.success ? "✓" : "✗"}
                                </span>{" "}
                                {tool.tool}
                                {tool.summary ? (
                                  <span className="text-muted-foreground"> — {truncateTraceDetailText(tool.summary, 120)}</span>
                                ) : null}
                                {outcomeEntries.length > 0 ? (
                                  <span
                                    className="mt-0.5 flex flex-wrap gap-1"
                                    aria-label={t("studioData.tabs.trace.toolOutcome")}
                                    title={t("studioData.tabs.trace.toolOutcome")}
                                  >
                                    {outcomeEntries.map((entry) => (
                                      <span
                                        key={entry}
                                        className="rounded border border-border/40 bg-background/50 px-1 font-mono text-[10px] text-muted-foreground"
                                      >
                                        {entry}
                                      </span>
                                    ))}
                                  </span>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {data.orchestratorTrace.ragChunkTitles.length > 0 && (
                      <p className="text-muted-foreground">
                        {t("studioData.tabs.trace.ragChunks")}: {data.orchestratorTrace.ragChunkTitles.join(", ")}
                      </p>
                    )}
                    {data.orchestratorTrace.decisionSteps.length > 0 && (
                      <div>
                        <p className="font-medium text-muted-foreground mb-1">{t("studioData.tabs.trace.decisionSteps")}</p>
                        <ol className="space-y-1.5 list-none">
                          {data.orchestratorTrace.decisionSteps.map((step) => {
                            const display = decisionStepDisplay(
                              step,
                              t("studioData.tabs.trace.privacyHiddenContent"),
                            );
                            const detailKey = `decision-step-${step.id}`;
                            return (
                              <li key={step.id} className="rounded-md border border-border/30 bg-background/50 px-2 py-1.5">
                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{step.phase}</span>
                                <p className="font-medium text-foreground">{step.title}</p>
                                <p className="text-muted-foreground">{display.summary}</p>
                                {display.technicalDetail ? (
                                  <div className="pt-0.5">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-1 text-[10px] text-muted-foreground"
                                      aria-expanded={expandedTechnicalDetails[detailKey] ?? false}
                                      onClick={() =>
                                        setExpandedTechnicalDetails((prev) => ({
                                          ...prev,
                                          [detailKey]: !prev[detailKey],
                                        }))
                                      }
                                    >
                                      {t("studioData.tabs.trace.technicalDetails")}
                                    </Button>
                                    {expandedTechnicalDetails[detailKey] ? (
                                      <p className="mt-0.5 text-muted-foreground font-mono text-[10px] break-all">
                                        {truncateTraceDetailText(display.technicalDetail, 400)}
                                      </p>
                                    ) : null}
                                  </div>
                                ) : null}
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Unified timeline */}
            <div className="px-5 pb-4">
              <div className={cn("relative overflow-y-auto scrollbar-thin pr-1", timelineMaxH)}>
                <div className="absolute left-[7px] top-0 bottom-0 w-px bg-border/40" />
                <div className="space-y-0">
                  {timeline.map((item) => (
                    <div key={item.id} className="relative pl-7 pb-3">
                      <div
                        className={cn(
                          "absolute left-[3px] top-[6px] h-[9px] w-[9px] rounded-full border-2",
                          item.isFailed
                            ? "border-destructive bg-destructive/20"
                            : item.isCompleted
                              ? "border-success bg-success/20"
                              : item.kind === "usage"
                                ? "border-primary/60 bg-primary/15"
                                : "border-muted-foreground/40 bg-background"
                        )}
                      />
                      <div className="grid grid-cols-[auto_1fr] gap-x-2 text-[12px]">
                        <span className="text-muted-foreground whitespace-nowrap">{item.time}</span>
                        <div className="min-w-0 space-y-0.5">
                          {item.kind === "audit" && item.tierDecision && (
                            <div className="space-y-0.5">
                              <div className="flex items-start gap-2 flex-wrap">
                                <span
                                  className={cn(
                                    "font-medium whitespace-nowrap",
                                    item.isFailed
                                      ? "text-destructive"
                                      : item.isCompleted
                                        ? "text-success"
                                        : "text-foreground"
                                  )}
                                >
                                  {t("studioData.tabs.trace.tier.decisionLabel")}
                                </span>
                                <span className="shrink-0 rounded-full bg-muted/60 px-1.5 py-0 text-[10px] font-medium text-muted-foreground">
                                  {t(`studioData.tabs.trace.tier.name.${item.tierDecision.tier}`, {
                                    defaultValue: item.tierDecision.tier,
                                  })}
                                </span>
                                <span
                                  className={cn(
                                    "shrink-0 rounded-full px-1.5 py-0 text-[10px] font-medium",
                                    item.tierDecision.outcome === "success"
                                      ? "bg-success/10 text-success"
                                      : item.tierDecision.outcome === "fail"
                                        ? "bg-destructive/10 text-destructive"
                                        : "bg-muted/60 text-muted-foreground"
                                  )}
                                >
                                  {t(`studioData.tabs.trace.tier.outcome.${item.tierDecision.outcome}`)}
                                </span>
                                {typeof item.tierDecision.durationMs === "number" && item.tierDecision.durationMs > 0 && (
                                  <span className="text-[10px] text-muted-foreground tabular-nums">
                                    {item.tierDecision.durationMs >= 1000
                                      ? `${(item.tierDecision.durationMs / 1000).toFixed(1)}s`
                                      : `${item.tierDecision.durationMs}ms`}
                                  </span>
                                )}
                                {item.tierDecision.reasonCode && (
                                  <span className="text-[10px] font-mono text-muted-foreground" title={item.tierDecision.reasonCode}>
                                    {t(`studioData.tabs.trace.tier.reason.${item.tierDecision.reasonCode}`, {
                                      defaultValue: item.tierDecision.reasonCode,
                                    })}
                                  </span>
                                )}
                                {item.tierDecision.executionPath && (
                                  <span className="text-[10px] text-muted-foreground/80">
                                    {item.tierDecision.executionPath}
                                  </span>
                                )}
                              </div>
                              {item.cascadeOf && (
                                <p className="text-[10px] text-muted-foreground font-mono truncate" title={item.cascadeOf}>
                                  {t("studioData.tabs.trace.cascadeOf", { rootCauseId: item.cascadeOf })}
                                </p>
                              )}
                            </div>
                          )}
                          {item.kind === "audit" && !item.tierDecision && (
                            <div className="space-y-0.5">
                              {item.humanSummary ? (
                                <>
                                  <p
                                    className={cn(
                                      "text-[12px] leading-snug",
                                      item.isFailed
                                        ? "text-destructive"
                                        : item.isCompleted
                                          ? "text-success"
                                          : "text-foreground"
                                    )}
                                  >
                                    {item.humanSummary}
                                  </p>
                                  {item.metaStr ? (
                                    <div className="pt-0.5">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-1 text-[10px] text-muted-foreground"
                                        aria-expanded={expandedTechnicalDetails[item.id] ?? false}
                                        onClick={() =>
                                          setExpandedTechnicalDetails((prev) => ({
                                            ...prev,
                                            [item.id]: !prev[item.id],
                                          }))
                                        }
                                      >
                                        {t("studioData.tabs.trace.technicalDetails")}
                                      </Button>
                                      {expandedTechnicalDetails[item.id] ? (
                                        <p className="text-[10px] text-muted-foreground font-mono break-words mt-0.5">
                                          {item.metaStr}
                                        </p>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </>
                              ) : (
                                <div className="flex items-start gap-2">
                                  <span
                                    className={cn(
                                      "font-medium",
                                      item.isFailed
                                        ? "text-destructive"
                                        : item.isCompleted
                                          ? "text-success"
                                          : "text-foreground"
                                    )}
                                  >
                                    {item.eventType?.replace(/_/g, " ")}
                                  </span>
                                  {item.metaStr ? (
                                    <span className="text-muted-foreground truncate">({item.metaStr})</span>
                                  ) : null}
                                </div>
                              )}
                              {item.cascadeOf && (
                                <p className="text-[10px] text-muted-foreground font-mono truncate" title={item.cascadeOf}>
                                  {t("studioData.tabs.trace.cascadeOf", { rootCauseId: item.cascadeOf })}
                                </p>
                              )}
                            </div>
                          )}
                          {item.kind === "llm" && (
                            <>
                              <div className="flex items-start gap-2 flex-wrap">
                                <span
                                  className={cn(
                                    "font-medium whitespace-nowrap shrink-0",
                                    item.isFailed
                                      ? "text-destructive"
                                      : item.isCompleted
                                        ? "text-success"
                                        : "text-foreground"
                                  )}
                                >
                                  {item.action?.replace(/_/g, " ")}
                                  {item.stepIndex != null && (
                                    <span className="text-muted-foreground font-normal ml-1">#{item.stepIndex}</span>
                                  )}
                                </span>
                                {item.tier && (
                                  <span className="shrink-0 rounded-full bg-muted/60 px-1.5 py-0 text-[10px] text-muted-foreground">
                                    {item.tier}
                                  </span>
                                )}
                                {item.model && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0 text-[10px] font-mono text-primary/80">
                                    {item.model}
                                  </span>
                                )}
                                {(item.temperature != null || item.maxTokens != null) && (
                                  <span className="text-[10px] text-muted-foreground shrink-0">
                                    {item.temperature != null
                                      ? t("studioData.tabs.trace.llmTemperature", { value: item.temperature })
                                      : null}
                                    {item.temperature != null && item.maxTokens != null ? " · " : null}
                                    {item.maxTokens != null
                                      ? t("studioData.tabs.trace.llmMaxTokens", { value: item.maxTokens })
                                      : null}
                                  </span>
                                )}
                                <span className="text-foreground truncate min-w-0" title={item.description}>
                                  {item.description}
                                </span>
                                {(item.executionRoute ||
                                  item.interactionMode ||
                                  item.commandsExecuted?.length ||
                                  item.systemPrompt ||
                                  item.userPromptText ||
                                  item.responseText) && (
                                  <button
                                    type="button"
                                    className="ml-auto shrink-0 text-[10px] text-primary hover:underline"
                                    onClick={() =>
                                      setExpandedTimelineItems((prev) => ({
                                        ...prev,
                                        [item.id]: !prev[item.id],
                                      }))
                                    }
                                  >
                                    {expandedTimelineItems[item.id]
                                      ? t("studioData.tabs.trace.collapseDetails")
                                      : t("studioData.tabs.trace.expandDetails")}
                                  </button>
                                )}
                              </div>
                              {(item.executionRoute || item.interactionMode || item.subFlow || item.method) && (
                                <p className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">
                                  {item.executionRoute ? (
                                    <span>{t("studioData.tabs.trace.executionRoute")}: {item.executionRoute}</span>
                                  ) : null}
                                  {item.interactionMode ? (
                                    <span>{t("studioData.tabs.trace.interactionMode")}: {item.interactionMode}</span>
                                  ) : null}
                                  {item.subFlow ? (
                                    <span>{t("studioData.tabs.trace.subFlow")}: {item.subFlow}</span>
                                  ) : null}
                                  {item.method ? (
                                    <span>{t("studioData.tabs.trace.method")}: {item.method}</span>
                                  ) : null}
                                  {item.llmConfidence != null ? (
                                    <span>{t("studioData.tabs.trace.confidence")}: {item.llmConfidence}</span>
                                  ) : null}
                                  {item.selfHealed ? (
                                    <span>{t("studioData.tabs.trace.selfHealed")}</span>
                                  ) : null}
                                </p>
                              )}
                              {item.selector && (
                                <p className="text-[11px] font-mono text-muted-foreground truncate" title={item.selector}>
                                  {item.selector}
                                </p>
                              )}
                              {item.error && (
                                <p className="text-[11px] text-destructive break-words">{item.error}</p>
                              )}
                              {((item.totalTokens != null && item.totalTokens > 0) ||
                                (item.durationMs != null && item.durationMs > 0)) && (
                                <p className="text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                                  {item.durationMs != null && item.durationMs > 0 && (
                                    <span>
                                      {item.durationMs >= 1000
                                        ? t("studioData.tabs.trace.durationSeconds", {
                                            n: (item.durationMs / 1000).toFixed(1),
                                          })
                                        : t("studioData.tabs.trace.durationMs", { n: item.durationMs })}
                                    </span>
                                  )}
                                  {item.totalTokens != null && item.totalTokens > 0 && (
                                    <>
                                      <span>
                                        {t("studioData.tabs.trace.tokensSummary", {
                                          input: item.inputTokens?.toLocaleString(i18n.language) ?? "",
                                          output: item.outputTokens?.toLocaleString(i18n.language) ?? "",
                                          total: item.totalTokens?.toLocaleString(i18n.language) ?? "",
                                        })}
                                      </span>
                                      {item.estimatedCostUsd != null && (
                                        <span className="font-medium">
                                          ${item.estimatedCostUsd < 0.01
                                            ? item.estimatedCostUsd.toFixed(4)
                                            : item.estimatedCostUsd.toFixed(3)}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </p>
                              )}
                              {expandedTimelineItems[item.id] && (
                                <div className="mt-1 space-y-1 rounded-md border border-border/30 bg-muted/10 p-2 text-[10px] text-muted-foreground">
                                  {item.commandsExecuted?.map((cmd) => (
                                    <p key={cmd} className="font-mono break-all">
                                      {cmd}
                                    </p>
                                  ))}
                                  {item.systemPrompt ? (
                                    <p>
                                      <span className="font-medium">{t("studioData.tabs.trace.systemPrompt")}: </span>
                                      {truncateTraceDetailText(item.systemPrompt)}
                                    </p>
                                  ) : null}
                                  {item.userPromptText ? (
                                    <p>
                                      <span className="font-medium">{t("studioData.tabs.trace.userPrompt")}: </span>
                                      {truncateTraceDetailText(item.userPromptText)}
                                    </p>
                                  ) : null}
                                  {item.responseText ? (
                                    <p>
                                      <span className="font-medium">{t("studioData.tabs.trace.llmResponse")}: </span>
                                      {truncateTraceDetailText(item.responseText)}
                                    </p>
                                  ) : null}
                                </div>
                              )}
                              {item.screenshots.length > 0 && (
                                <div className="flex gap-2 mt-1">
                                  {item.screenshots.map(({ key, url, label }) =>
                                    url ? (
                                      <a
                                        key={key}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block rounded-md border border-border/40 overflow-hidden hover:ring-2 hover:ring-primary/30 transition-all"
                                      >
                                        <img
                                          src={url}
                                          alt={label}
                                          className="w-[140px] h-auto max-h-[90px] object-contain bg-black/5"
                                          loading="lazy"
                                        />
                                      </a>
                                    ) : null
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {item.kind === "usage" && (
                            <div className="space-y-1">
                              <div className="flex items-start gap-2 flex-wrap">
                                <span className="font-medium text-primary whitespace-nowrap shrink-0">
                                  {item.operation?.replace(/_/g, " ")}
                                </span>
                                {item.model && (
                                  <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-0 text-[10px] text-primary/70 font-mono">
                                    {item.model}
                                  </span>
                                )}
                                {(item.temperature != null || item.maxTokens != null) && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {item.temperature != null
                                      ? t("studioData.tabs.trace.llmTemperature", { value: item.temperature })
                                      : null}
                                    {item.temperature != null && item.maxTokens != null ? " · " : null}
                                    {item.maxTokens != null
                                      ? t("studioData.tabs.trace.llmMaxTokens", { value: item.maxTokens })
                                      : null}
                                  </span>
                                )}
                                <span className="text-muted-foreground text-[11px]">
                                  {t("studioData.tabs.trace.tokensSummary", {
                                    input: item.inputTokens?.toLocaleString(i18n.language) ?? "",
                                    output: item.outputTokens?.toLocaleString(i18n.language) ?? "",
                                    total: item.totalTokens?.toLocaleString(i18n.language) ?? "",
                                  })}
                                  {item.estimatedCostUsd != null && (
                                    <span className="font-medium ml-1 text-foreground">
                                      ${formatTraceUsd(item.estimatedCostUsd)}
                                    </span>
                                  )}
                                </span>
                                {(item.intent ||
                                  item.promptSummary ||
                                  item.reasoningExcerpt ||
                                  item.promptHash) && (
                                  <button
                                    type="button"
                                    className="ml-auto shrink-0 text-[10px] text-primary hover:underline"
                                    onClick={() =>
                                      setExpandedTimelineItems((prev) => ({
                                        ...prev,
                                        [item.id]: !prev[item.id],
                                      }))
                                    }
                                  >
                                    {expandedTimelineItems[item.id]
                                      ? t("studioData.tabs.trace.collapseDetails")
                                      : t("studioData.tabs.trace.expandDetails")}
                                  </button>
                                )}
                              </div>
                              {(item.intent || item.confidence != null || item.promptKey) && (
                                <p className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">
                                  {item.intent ? (
                                    <span>
                                      {t("studioData.tabs.trace.intent")}:{" "}
                                      <span className="font-medium text-foreground">{item.intent}</span>
                                    </span>
                                  ) : null}
                                  {item.confidence != null ? (
                                    <span>
                                      {t("studioData.tabs.trace.confidence")}: {(item.confidence * 100).toFixed(0)}%
                                    </span>
                                  ) : null}
                                  {item.promptKey ? (
                                    <span>
                                      {t("studioData.tabs.trace.llmPromptKey")}:{" "}
                                      <span className="font-mono">{item.promptKey}</span>
                                    </span>
                                  ) : null}
                                  {item.promptKind ? (
                                    <span>
                                      {t("studioData.tabs.trace.promptKind")}: {item.promptKind}
                                    </span>
                                  ) : null}
                                </p>
                              )}
                              {expandedTimelineItems[item.id] && (
                                <div className="rounded-md border border-border/30 bg-muted/10 p-2 text-[10px] text-muted-foreground space-y-1">
                                  {item.promptSummary ? (
                                    <p>
                                      <span className="font-medium">{t("studioData.tabs.trace.llmPromptSummary")}: </span>
                                      {item.promptSummary}
                                    </p>
                                  ) : null}
                                  {item.promptHash ? (
                                    <p className="font-mono break-all">
                                      hash: {shortTraceHash(item.promptHash)}
                                    </p>
                                  ) : null}
                                  {item.reasoningExcerpt ? (
                                    <p>
                                      <span className="font-medium">{t("studioData.tabs.trace.llmReasoning")}: </span>
                                      {truncateTraceDetailText(item.reasoningExcerpt, 400)}
                                    </p>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Failure screenshot */}
            {data.presignedUrls?.failureScreenshot && (
              <div className="border-t border-border/50 px-5 py-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-destructive mb-2">
                  {t("studioData.tabs.trace.failureHeading")}
                </p>
                <a
                  href={data.presignedUrls.failureScreenshot}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block rounded-lg border border-destructive/40 overflow-hidden max-w-xl hover:ring-2 hover:ring-destructive/30 transition-all"
                >
                  <img
                    src={data.presignedUrls.failureScreenshot}
                    alt={t("studioData.tabs.trace.failureAlt")}
                    className="w-full h-auto object-contain bg-black/5"
                  />
                </a>
              </div>
            )}

            {!data.presignedUrls && (
              <div className="border-t border-border/50 px-5 py-3">
                <p className="text-[12px] text-muted-foreground">{t("studioData.tabs.trace.s3NotConfigured")}</p>
              </div>
            )}
          </div>
        );
      })()}
    </>
  );

  if (layout === "page") {
    return <div className="mx-auto max-w-[1200px] animate-fade-in space-y-6 px-1">{inner}</div>;
  }

  return <div className="space-y-4 animate-fade-in">{inner}</div>;
}
